import { Action, Reducer } from "redux";
import { ApplicationState, AppThunkAction, AppThunkActionAsync } from "../..";
import { AppServices } from "../../../AppServices";
import { Task, TaskFilterType, TaskManagementState, TaskManagementApiRequest, TaskStatus, TaskRemindersState, TaskManagementLinkedItem, TaskManagementLinkedItemType, TaskSortableColumns } from "../../Models/TaskManagement/TaskManagementDataModel";
import { ToxnotServiceClient } from "../../ToxnotServiceClient";

const TaskManagementSetState = "SET_TASK_DATA";
interface TaskManagementSetState {
    type: typeof TaskManagementSetState,
    value: Partial<TaskManagementState>
}

const initialState: TaskManagementState = {
    tasks: {},
    filteredAndSortedTaskIds: [],
    selectedTaskIds: {},
    openTaskId: null,
    showErrorMessage: false,
    dataLoaded: false,
    taskFilters: {
        status: [TaskStatus.Active, TaskStatus.New],
        search: [],
        assignedUserId: [],
        deadlineDateAfter: [],
        deadlineDateBefore: []
    },
    searchBarInput: "",
    teamMembers: [],
    surveyList: [],
    inventoryIdToInventoryNamesMap: {},
    filesTable: {},
    sortSettings: {
        activeSortColumn: TaskSortableColumns.DueDate,
        sortAscending: true
    }
}

//==============================================

export function getDateIsoString(dateInput : string): string {
    const splits: number[] = dateInput.split('-').map(s => parseInt(s));
    const date = new Date(splits[0], splits[1] - 1, splits[2]);
    return date.toISOString();
}

export function stringToTaskStatusEnumMap(): { [str: string]: TaskStatus } {
    return {
        ["New"]: TaskStatus.New,
        ["Active"]: TaskStatus.Active,
        ["Closed"]: TaskStatus.Closed
    }
}

export function  getLinkedItemLink(linkedItem: TaskManagementLinkedItem) {
    switch (linkedItem.itemType) {
        case TaskManagementLinkedItemType.Inventory:
            return "/Data/Inventory?inventoryId=" + linkedItem.itemId;
        case TaskManagementLinkedItemType.Survey:
            return "/Survey/" + linkedItem.itemId;
        case TaskManagementLinkedItemType.CustomerRequest:
            return "/MyCustomers/Browse/Customer/" + linkedItem.secondaryItemId + "/Request/" + linkedItem.itemId;
    }

}

// =============================================

type KnownActions = TaskManagementSetState;

export const actionCreators = {
    setTaskManagementState: (value: Partial<TaskManagementState>): AppThunkAction<KnownActions> => (dispatch, getState) => {
        const state = getState();
        if (!state) {
            return;
        }

        dispatch({ 
            type: TaskManagementSetState,
            value: value
        });
    },
    addTask: (appServices: AppServices): AppThunkAction<KnownActions> => (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) {
            return;
        }

        const newTask: TaskManagementApiRequest = {
            taskId: null,
            title: "",
            description: "",
            assignedUserId: appServices.userContext?.userId ?? null,
            status: TaskStatus.New,
            deadlineDate: null,
            linkedItems: [],
            weeklyReminderState: TaskRemindersState.NeedsToBeSent,
            dailyReminderState: TaskRemindersState.NeedsToBeSent,
            weeklyPeekLockTimeout: null,
            dailyPeekLockTimeout: null,
            assignedByUserId: null,
            attachedFileIds: [],
            sendAssignmentReminder: false
        }

        updateTask(newTask, true, dispatch, state, state.appServices?.userContext?.teamOwnerId ?? "");
    },
    deleteTasks: (selectedTaskIds: {[taskId: string] : string } ): AppThunkAction<KnownActions> => (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) {
            return;
        }

        var tasks = { ...state.taskManagementState.tasks };

        for (const key of Object.keys(selectedTaskIds)) {
            delete tasks[key];
        }


        dispatch({
            type: TaskManagementSetState,
            value: {
                ...state.taskManagementState,
                tasks: tasks,
                selectedTaskIds: {}
            }
        });

        deleteTask(Object.keys(selectedTaskIds), dispatch, state);
    },
    modifyTaskState: (taskId: string, taskChanges: Partial<Task>, originalTask: Task): AppThunkAction<KnownActions> => (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) {
            return;
        }

        const newTask : Task = {
            ...originalTask,
            ...taskChanges
        }

        dispatch({
            type: TaskManagementSetState,
            value: {
                ...state.taskManagementState,
                tasks: {
                    ...state.taskManagementState.tasks,
                    [taskId]: newTask
                }
            }
        });

        const newTaskApiRequest: TaskManagementApiRequest = {
            ...newTask,
            sendAssignmentReminder: false
        }

        updateTask(newTaskApiRequest, false, dispatch, state, state.appServices?.userContext?.teamOwnerId ?? "");
    },
    modifyTaskAssignedToState: (taskId: string, assignedToUserId: string, originalTask: Task): AppThunkAction<KnownActions> => (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState || !state.appServices?.userContext) {
            return;
        }

        const newTask: Task = {
            ...originalTask,
            assignedUserId: assignedToUserId,
            assignedByUserId: state.appServices.userContext.userId
        }

        dispatch({
            type: TaskManagementSetState,
            value: {
                ...state.taskManagementState,
                tasks: {
                    ...state.taskManagementState.tasks,
                    [taskId]: newTask
                }
            }
        });

        const newTaskApiRequest: TaskManagementApiRequest = {
            ...newTask,
            sendAssignmentReminder: true
        }

        updateTask(newTaskApiRequest, false, dispatch, state, state.appServices?.userContext?.teamOwnerId ?? "");
    },
    modifyTaskDueDate: (taskId: string, dueDate: string, originalTask: Task): AppThunkAction<KnownActions> => (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) {
            return;
        }

        const newTask: Task = {
            ...originalTask,
            deadlineDate: dueDate,
            weeklyPeekLockTimeout: null,
            dailyPeekLockTimeout: null,
            dailyReminderState: TaskRemindersState.NeedsToBeSent,
            weeklyReminderState: TaskRemindersState.NeedsToBeSent
        }

        dispatch({
            type: TaskManagementSetState,
            value: {
                ...state.taskManagementState,
                tasks: {
                    ...state.taskManagementState.tasks,
                    [taskId]: newTask
                }
            }
        });

        const newTaskApiRequest: TaskManagementApiRequest = {
            ...newTask,
            sendAssignmentReminder: false
        }

        updateTask(newTaskApiRequest, false, dispatch, state, state.appServices?.userContext?.teamOwnerId ?? "");
    },
    getTasks: (): AppThunkActionAsync<KnownActions> => async (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) {
            return;
        }

        if (!state.appServices) {
            return;
        }

        const serviceClient = new ToxnotServiceClient();
        const apiResult = await serviceClient.getTaskManagementTasks();

        if (!apiResult.success || !apiResult.data) {
            state.appServices.displayApiErrorMessage();
            return;
        }

        const data = apiResult.data;
        const tasks = data.tasks;
        
        const filteredTaskIds = filterTasks(tasks, state.taskManagementState.taskFilters);
        const filteredAndSortedTasks = sortTasks(tasks, filteredTaskIds, state.taskManagementState.sortSettings.activeSortColumn, state.taskManagementState.sortSettings.sortAscending);

        dispatch({
            type: TaskManagementSetState,
            value: {
                tasks: tasks,
                filteredAndSortedTaskIds: filteredAndSortedTasks,
                dataLoaded: true,
                teamMembers: data.taskAssignedUsers,
                inventoryIdToInventoryNamesMap: data.inventoryIdToInventoryNamesMap,
                surveyList: data.linkableDataCollectionRequests,
                filesTable: data.fileIdsToFileNameMap
            }
        });

        return;
        
    },
    bulkUpdateTasks: (selectedTaskIds: {[taskId: string] : string}, changes: Partial<Task>): AppThunkActionAsync<KnownActions> => async (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) {
            return;
        }

        if (!state.appServices) {
            return;
        }

        let newTasks: {[taskId: string] : Task} = {};

        for (let key of Object.keys(selectedTaskIds)) {
            newTasks[key] = {
                ...state.taskManagementState.tasks[key],
                ...changes
            }
        }

        const serviceClient = new ToxnotServiceClient();

        const apiResult = await serviceClient.bulkUpdateTaskManagementTask(Object.values(newTasks), "");

        if (!apiResult.success) {
            state.appServices.displayApiErrorMessage();
            return;
        }

        dispatch({
            type: TaskManagementSetState,
            value: {
                tasks: {
                    ...state.taskManagementState.tasks,
                    ...newTasks
                }
            }
        })

    },
    bulkUpdateTasksWithAssignedTo: (selectedTaskIds: { [taskId: string]: string }, assignedTo: string): AppThunkActionAsync<KnownActions> => async (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState || !state.appServices?.userContext) {
            return;
        }

        if (!state.appServices) {
            return;
        }

        let newTasks: { [taskId: string]: Task } = {};

        for (let key of Object.keys(selectedTaskIds)) {
            newTasks[key] = {
                ...state.taskManagementState.tasks[key],
                assignedUserId: assignedTo,
                assignedByUserId: state.appServices.userContext.userId
            }
        }

        const serviceClient = new ToxnotServiceClient();

        const apiResult = await serviceClient.bulkUpdateTaskManagementTask(Object.values(newTasks), "true");

        if (!apiResult.success) {
            state.appServices.displayApiErrorMessage();
            return;
        }

        dispatch({
            type: TaskManagementSetState,
            value: {
                tasks: {
                    ...state.taskManagementState.tasks,
                    ...newTasks
                }
            }
        })

    },
    bulkUpdateDueDateForTasks: (selectedTaskIds: { [taskId: string]: string }, dueDate: string): AppThunkActionAsync<KnownActions> => async (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) {
            return;
        }

        if (!state.appServices) {
            return;
        }

        let newTasks: { [taskId: string]: Task } = {};

        for (let key of Object.keys(selectedTaskIds)) {
            newTasks[key] = {
                ...state.taskManagementState.tasks[key],
                deadlineDate: dueDate,
                weeklyPeekLockTimeout: null,
                dailyPeekLockTimeout: null,
                dailyReminderState: TaskRemindersState.NeedsToBeSent,
                weeklyReminderState: TaskRemindersState.NeedsToBeSent
            }
        }

        const serviceClient = new ToxnotServiceClient();

        const apiResult = await serviceClient.bulkUpdateTaskManagementTask(Object.values(newTasks),"");

        if (!apiResult.success) {
            state.appServices.displayApiErrorMessage();
            return;
        }

        dispatch({
            type: TaskManagementSetState,
            value: {
                tasks: {
                    ...state.taskManagementState.tasks,
                    ...newTasks
                }
            }
        })

    },
    filterTasks: (): AppThunkAction<KnownActions> => (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) return;

        const taskState = state.taskManagementState;
        const filters = state.taskManagementState.taskFilters;
        let tasks = filterTasks(taskState.tasks, filters);

        dispatch({
            type: TaskManagementSetState,
            value: {
                filteredAndSortedTaskIds: tasks
            }
        });
        

    },
    sortTasks: (column: TaskSortableColumns, sortAscending: boolean): AppThunkAction<KnownActions> => (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) return;

        const taskState = state.taskManagementState;
        let tasks = sortTasks(taskState.tasks, taskState.filteredAndSortedTaskIds, column, sortAscending);

        dispatch({
            type: TaskManagementSetState,
            value: {
                filteredAndSortedTaskIds: tasks,
                sortSettings: {
                    activeSortColumn: column,
                    sortAscending: sortAscending
                }
            }
        });


    },
    getLinkedInventoriesNames: (inventoryIds: string[]): AppThunkActionAsync<KnownActions> => async (dispatch, getState) => {
        const state = getState();
        if (!state || !state.taskManagementState) {
            return;
        }

        if (!state.appServices) {
            return;
        }

        const serviceClient = new ToxnotServiceClient();

        const apiResult = await serviceClient.getTaskManagementLinkedInventoriesNames(inventoryIds);

        if (!apiResult.success || !apiResult.data) {
            state.appServices.displayApiErrorMessage();
            return;
        }

        const data = apiResult.data;

        dispatch({
            type: TaskManagementSetState,
            value: {
                inventoryIdToInventoryNamesMap: {
                    ...state.taskManagementState.inventoryIdToInventoryNamesMap,
                    ...data 
                }
            }
        });

    },
    uploadFiles: (taskId: string, files: FileList): AppThunkActionAsync<KnownActions> => async (dispatch, getState) => {

        const state = getState();
        if (!state || !state.taskManagementState) {
            return;
        }

        if (!state.appServices) {
            return;
        }

        const serviceClient = new ToxnotServiceClient();
        let fileTable: { [fileId: string]: string } = {};

        for (let i = 0; i < files.length; i++) {

            const file = files.item(i);

            if (file != null) {
                const apiResult = await serviceClient.uploadGeneralFile(file.name, "Uploaded To Task", 0, false, false, file);
                console.log(apiResult);
                if (!apiResult.success || !apiResult.data) {
                    state.appServices.displayApiErrorMessage();
                    return;
                }
                console.log(apiResult);
                fileTable[apiResult.data.fileId] = apiResult.data.filename;
            }

        }   

        const newTask: Task = {
            ...state.taskManagementState.tasks[taskId],
            attachedFileIds: state.taskManagementState.tasks[taskId].attachedFileIds.concat(Object.keys(fileTable))
        }

        const newTaskApiRequest: TaskManagementApiRequest = {
            ...newTask,
            sendAssignmentReminder: false
        }

        await updateTask(newTaskApiRequest, false, dispatch, state, state.appServices?.userContext?.teamOwnerId ?? "");

        dispatch({
            type: TaskManagementSetState,
            value: {
                ...state.taskManagementState,
                tasks: {
                    ...state.taskManagementState.tasks,
                    [taskId]: newTask
                },
                filesTable: {
                    ...state.taskManagementState.filesTable,
                    ...fileTable
                }
            }
        });


    }
}

//Function to update an exisitng task or create a new one. Set modifyingNewTask = true to create a new task.
async function updateTask(taskRequest: TaskManagementApiRequest, modifyingNewTask: boolean, dispatch: (action: KnownActions) => void, state: ApplicationState, ownerId: string, sendAssignmentEmail = false) {
    if (!state || !state.taskManagementState) {
        return;
    }

    if (!state.appServices) {
        return;
    }

    const serviceClient = new ToxnotServiceClient();

    const apiResult = await serviceClient.updateTaskManagementTask(taskRequest, sendAssignmentEmail);

    if (!apiResult.success) {
        state.appServices.displayApiErrorMessage();
        return;
    } 

    //If we added a new Task with a temprorary task id, then add the C# assigned guid to it
    if (modifyingNewTask) {

        const newTaskId = apiResult.data;

        if (!newTaskId) {
            dispatch({
                type: TaskManagementSetState,
                value: {
                    showErrorMessage: true
                }
            });
            return;
        }

        const task: Task = {
            ...taskRequest,
            taskId: newTaskId,
            teamOwnerId: ownerId
        }

        dispatch({
            type: TaskManagementSetState,
            value: {
                ...state,
                tasks: { [newTaskId]: task, ...state.taskManagementState.tasks },
                filteredAndSortedTaskIds: [newTaskId, ...state.taskManagementState.filteredAndSortedTaskIds],
            }
        });
    }
}

//Calls the api to delete the tasks with the given task ids.
async function deleteTask(taskIds: string[], dispatch: (action: KnownActions) => void, state: ApplicationState) {
    if (!state || !state.taskManagementState) {
        return;
    }

    if (!state.appServices) {
        return;
    }

    const serviceClient = new ToxnotServiceClient();
    const apiResult = await serviceClient.deleteTaskManagementTask(taskIds);

    if (!apiResult.success) {
        state.appServices.displayApiErrorMessage();
        return;
    }
}

//Filters the provided tasks based on the provided filters. Filters have determinded type with possible string values. 
function filterTasks(tasks: { [taskId: string]: Task }, filters: { [filterType in TaskFilterType]: string[] }): string[] {

    let filteredTaskIds: string[] = [];

    for (const task of Object.values(tasks)) {

        let passedFilters = true;

        for (const filterType of Object.keys(filters)) {

            if (!filters[filterType as TaskFilterType]) continue;

            const values = filters[filterType as TaskFilterType];

            if (values.length < 1) continue;

            switch (filterType as TaskFilterType) {
                case TaskFilterType.Status:
                    passedFilters = passedFilters && (values.findIndex(v => v === task.status) >= 0);
                    continue;
                case TaskFilterType.UserId:
                    passedFilters = passedFilters && (values.findIndex(v => v === task.assignedUserId) >= 0)
                    continue;
                case TaskFilterType.Search:
                    passedFilters = passedFilters && task.title.toLowerCase().includes(values[0].toLowerCase())
                    continue;
                case TaskFilterType.DeadlineDateBefore:
                    passedFilters = task.deadlineDate !== null && (passedFilters && new Date(values[0]).getTime() >= new Date(task.deadlineDate).getTime())
                    continue;
                case TaskFilterType.DeadlineDateAfter:
                    passedFilters = task.deadlineDate !== null && (passedFilters && new Date(values[0]).getTime() <= new Date(task.deadlineDate).getTime())
                    continue;
                default:
                    continue;

            }
        }

        if (passedFilters) filteredTaskIds.push(task.taskId);
    }
    return filteredTaskIds;
}

function sortTasks(tasks: { [taskId: string]: Task }, filteredTasks: string[], sortColumn: TaskSortableColumns, sortAscending: boolean): string[] {
    switch (sortColumn) {
        case TaskSortableColumns.Title:
            return filteredTasks.sort((a, b) => sortAscending ? tasks[a].title.localeCompare(tasks[b].title) : -1 * tasks[a].title.localeCompare(tasks[b].title));
        case TaskSortableColumns.State:
            return filteredTasks.sort((a, b) => sortAscending ? tasks[a].status.localeCompare(tasks[b].status) : -1 * tasks[a].status.localeCompare(tasks[b].status));
        case TaskSortableColumns.DueDate:
            return filteredTasks.sort((a, b) => {
                //If null max date so that they appear at bottom
                const s_a = tasks[a].deadlineDate ?? new Date("9999/12/31").toISOString();
                const s_b = tasks[b].deadlineDate ?? new Date("9999/12/31").toISOString();

                const compare = s_a.localeCompare(s_b);
                return sortAscending ? compare : -1 * compare;
            });
    }
}

export const reducer: Reducer<TaskManagementState> = (state: TaskManagementState | undefined, action: Action): TaskManagementState => {
    if (state === undefined) {
        return initialState;
    }
    let incomingAction;
    switch (action.type) {
        case TaskManagementSetState:
             incomingAction = action as TaskManagementSetState;
            return {
                ...state,
                ...incomingAction.value
            }
    }
    return state;
}


